import React, { useMemo, useCallback, useReducer } from "react";
import useTables from "./hooks/useTables";
import TableCard from "./components/TableCard";
import CustomerDetailsForm from "./components/CustomerDetailsForm";
import NewOrderPage from "./components/NewOrderPage";
import CartOverlay from "./components/CartOverlay";
import CartPage from "./components/CartPage";
import MoveTableModal from "./components/MoveTableModal";
import TableOrderDetails from "./components/TableOrderDetails";
import OrderWithBillGen from "../OrderWithBillGen";
import { BasicTable, Table } from "./types";
import { FaSync, FaUtensils } from "react-icons/fa";
import { saveCustomerDetails } from "../../api/tableApi";

type CustomerDetails = {
  id?: string;
  customerName?: string;
  customerEmail?: string;
  customerMobile?: string;
  customerCount?: number;
  waiterName?: string;
};

type CartItem = {
  id: string;
  name: string;
  price: number;
  quantity: number;
  instructions?: string;
};

type ViewState = 'home' | 'new-order' | 'cart' | 'table-orders' | 'table-bill';
type FilterType = 'all' | 'available' | 'occupied';

type State = {
  selectedTableId: string | null;
  currentView: ViewState;
  previousView: ViewState;
  customerDetails: CustomerDetails | null;
  cartItems: CartItem[];
  filter: FilterType;
  openCustomerForm: boolean;
  openMoveModal: boolean;
  moveLoading: boolean;
  fullTableDetails: { [key: string]: Table };
};

type Action =
  | { type: 'SET_SELECTED_TABLE'; payload: string | null }
  | { type: 'SET_VIEW'; payload: ViewState }
  | { type: 'SET_PREVIOUS_VIEW'; payload: ViewState }
  | { type: 'SET_CUSTOMER_DETAILS'; payload: CustomerDetails | null }
  | { type: 'SET_CART_ITEMS'; payload: CartItem[] }
  | { type: 'SET_FILTER'; payload: FilterType }
  | { type: 'SET_OPEN_CUSTOMER_FORM'; payload: boolean }
  | { type: 'SET_OPEN_MOVE_MODAL'; payload: boolean }
  | { type: 'SET_MOVE_LOADING'; payload: boolean }
  | { type: 'SET_FULL_TABLE_DETAILS'; payload: { [key: string]: Table } };

const initialState: State = {
  selectedTableId: null,
  currentView: 'home',
  previousView: 'home',
  customerDetails: null,
  cartItems: [],
  filter: 'all',
  openCustomerForm: false,
  openMoveModal: false,
  moveLoading: false,
  fullTableDetails: {},
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_SELECTED_TABLE':
      return { ...state, selectedTableId: action.payload };
    case 'SET_VIEW':
      return { ...state, currentView: action.payload };
    case 'SET_PREVIOUS_VIEW':
      return { ...state, previousView: action.payload };
    case 'SET_CUSTOMER_DETAILS':
      return { ...state, customerDetails: action.payload };
    case 'SET_CART_ITEMS':
      return { ...state, cartItems: action.payload };
    case 'SET_FILTER':
      return { ...state, filter: action.payload };
    case 'SET_OPEN_CUSTOMER_FORM':
      return { ...state, openCustomerForm: action.payload };
    case 'SET_OPEN_MOVE_MODAL':
      return { ...state, openMoveModal: action.payload };
    case 'SET_MOVE_LOADING':
      return { ...state, moveLoading: action.payload };
    case 'SET_FULL_TABLE_DETAILS':
      return { ...state, fullTableDetails: action.payload };
    default:
      return state;
  }
}

const QuickServe: React.FC<{ restaurantGuid: string }> = ({ restaurantGuid }) => {
  const { tables, loading, error, refresh, moveTable } = useTables();
  const [state, dispatch] = useReducer(reducer, initialState);

  const selectedTable = state.selectedTableId ? state.fullTableDetails[state.selectedTableId] || null : null;

  // Computed values
  const cartItemCount = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const normalizedTables = useMemo(() => {
    const map = new Map<string, BasicTable>();

    tables.forEach((t) => {
      if (!t?.id) return;

      const isAvailable =
        t.isAvailable === true;

      map.set(t.id, {
        ...t,
        isAvailable,
      });
    });

    return Array.from(map.values());
  }, [tables]);


  const filteredTables = useMemo(() => {
    if (state.filter === "available") {
      return normalizedTables.filter(t => t.isAvailable);
    }

    if (state.filter === "occupied") {
      return normalizedTables.filter(t => !t.isAvailable);
    }

    return normalizedTables;
  }, [normalizedTables, state.filter]);


  const stats = useMemo(() => ({
    total: normalizedTables.length,
    available: normalizedTables.filter(t => t.isAvailable).length,
    occupied: normalizedTables.filter(t => !t.isAvailable).length,
  }), [normalizedTables]);

  // Event handlers
  const handleTableClick = useCallback(async (table: BasicTable) => {
    dispatch({ type: 'SET_SELECTED_TABLE', payload: table.id });
    dispatch({ type: 'SET_PREVIOUS_VIEW', payload: 'home' });

    if (table.isAvailable) {
      dispatch({ type: 'SET_OPEN_CUSTOMER_FORM', payload: true });
    } else {
      dispatch({ type: 'SET_VIEW', payload: 'table-orders' });
    }
  }, []);

  const handleCustomerDetailsSubmit = useCallback(async (details: CustomerDetails) => {
    try {
      const payload = {
        id: details.id || '',
        tableId: (state.selectedTableId || '').toString(),
        customerName: details.customerName,
        customerEmail: details.customerEmail,
        customerMobile: details.customerMobile,
        totalGuest: details.customerCount,
        waiterName: details.waiterName,
      };
      const response = await saveCustomerDetails(payload);
      dispatch({ type: 'SET_CUSTOMER_DETAILS', payload: response.data });
      dispatch({ type: 'SET_OPEN_CUSTOMER_FORM', payload: false });
      dispatch({ type: 'SET_PREVIOUS_VIEW', payload: 'home' });
      dispatch({ type: 'SET_VIEW', payload: 'new-order' });
    } catch (error) {
      console.error('Failed to save customer details', error);
      // Handle error, perhaps show toast
    }
  }, [state.selectedTableId]);

  const handleAddToCart = useCallback((menuItem: any, quantity: number) => {
    const existing = state.cartItems.find(item => item.id === menuItem.id);
    if (existing) {
      dispatch({
        type: 'SET_CART_ITEMS', payload: state.cartItems.map(item =>
          item.id === menuItem.id ? { ...item, quantity: item.quantity + quantity } : item
        )
      });
    } else {
      dispatch({
        type: 'SET_CART_ITEMS', payload: [...state.cartItems, {
          id: menuItem.id,
          name: menuItem.name,
          price: menuItem.discountPrice || menuItem.price,
          quantity,
          instructions: '',
        }]
      });
    }
  }, [state.cartItems]);

  const handleUpdateCartQuantity = useCallback((itemId: string, quantity: number) => {
    dispatch({ type: 'SET_CART_ITEMS', payload: state.cartItems.map(item => item.id === itemId ? { ...item, quantity } : item).filter(item => item.quantity > 0) });
  }, [state.cartItems]);

  const handleUpdateInstructions = useCallback((itemId: string, instructions: string) => {
    dispatch({ type: 'SET_CART_ITEMS', payload: state.cartItems.map(item => item.id === itemId ? { ...item, instructions } : item) });
  }, [state.cartItems]);

  const handlePlaceOrder = useCallback(() => {
    dispatch({ type: 'SET_CART_ITEMS', payload: [] });
    dispatch({ type: 'SET_VIEW', payload: 'home' });
    dispatch({ type: 'SET_SELECTED_TABLE', payload: null });
    dispatch({ type: 'SET_CUSTOMER_DETAILS', payload: null });
  }, []);

  const handleBack = useCallback(() => {
    dispatch({ type: 'SET_VIEW', payload: state.previousView });
    if (state.previousView === 'home') {
      dispatch({ type: 'SET_SELECTED_TABLE', payload: null });
      dispatch({ type: 'SET_CUSTOMER_DETAILS', payload: null });
      dispatch({ type: 'SET_OPEN_CUSTOMER_FORM', payload: false });
    }
  }, [state.previousView]);

  // Logic for existing orders
  const handleTableOrderNewOrder = useCallback((details?: CustomerDetails) => {
    dispatch({ type: 'SET_CUSTOMER_DETAILS', payload: details || { customerName: 'Guest', customerCount: 1 } });
    dispatch({ type: 'SET_PREVIOUS_VIEW', payload: 'table-orders' });
    dispatch({ type: 'SET_VIEW', payload: 'new-order' });
  }, []);

  // View Switcher (Mental Render)
  if (state.currentView === 'new-order') return (
    <div className="animate-in slide-in-from-right duration-300 h-screen flex flex-col">
      <div className="flex-1 overflow-y-auto overscroll-contain">
        <NewOrderPage
          customerDetails={state.customerDetails || {}}
          tableId={state.selectedTableId || ''}
          restaurantGuid={restaurantGuid}
          cartItems={state.cartItems}
          onBack={handleBack}
          onAddToCart={handleAddToCart}
          onUpdateCartQuantity={handleUpdateCartQuantity}
        />
      </div>

      <CartOverlay
        itemCount={cartItemCount}
        onViewCart={() => dispatch({ type: 'SET_VIEW', payload: 'cart' })}
        onClearCart={() => dispatch({ type: 'SET_CART_ITEMS', payload: [] })}
      />
    </div>
  );

  if (state.currentView === 'cart') return (
    <div className="animate-in slide-in-from-bottom duration-300 h-screen">
      <CartPage
        cartItems={state.cartItems}
        restaurantGuid={restaurantGuid}
        tableId={state.selectedTableId || ''}
        customerDetails={state.customerDetails || {}}
        onBack={() => dispatch({ type: 'SET_VIEW', payload: 'new-order' })}
        onUpdateQuantity={handleUpdateCartQuantity}
        onUpdateInstructions={handleUpdateInstructions}
        onRemoveItem={(id) => dispatch({ type: 'SET_CART_ITEMS', payload: state.cartItems.filter(i => i.id !== id) })}
        onPlaceOrder={handlePlaceOrder}
      />
    </div>
  );

  if (state.currentView === 'table-orders' && state.selectedTableId) return (
    <div className="animate-in fade-in duration-300">
      <TableOrderDetails
        tableId={state.selectedTableId}
        tableLabel={state.fullTableDetails[state.selectedTableId]?.name || `Table ${state.selectedTableId}`}
        onBack={() => { dispatch({ type: 'SET_VIEW', payload: 'home' }); dispatch({ type: 'SET_SELECTED_TABLE', payload: null }); }}
        onBill={() => dispatch({ type: 'SET_VIEW', payload: 'table-bill' })}
        onNewOrder={handleTableOrderNewOrder}
      />
    </div>
  );

  if (state.currentView === 'table-bill' && state.selectedTableId) return (
    <OrderWithBillGen tableId={state.selectedTableId} type="bill" onBillClosed={() => dispatch({ type: 'SET_VIEW', payload: 'table-orders' })} />
  );

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100 flex flex-col">
      {/* Responsive Premium Header */}
      <header className="sticky top-0 z-40 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/60 dark:border-gray-100/5 px-3 sm:px-6 py-2 sm:py-4 transition-all">
        <div className="max-w-7xl mx-auto flex justify-between items-center">

          {/* Dynamic Branding Group */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-tr from-orange-500 to-amber-400 rounded-lg sm:rounded-xl shadow-lg shadow-orange-500/20 flex items-center justify-center text-white transition-all">
                <FaUtensils className="text-sm sm:text-lg" />
              </div>
              {loading && (
                <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2 sm:h-3 sm:w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 sm:h-3 sm:w-3 bg-orange-500"></span>
                </span>
              )}
            </div>

            <div className="flex flex-col justify-center">
              <h1 className="text-base sm:text-xl font-black tracking-tight text-gray-900 dark:text-gray-100 leading-tight">
                QuickServe
              </h1>
            </div>
          </div>

          {/* Small Sync Button for Mobile */}
          <button
            onClick={refresh}
            disabled={loading}
            className={`
        p-2 sm:p-2.5 rounded-full transition-all active:scale-90
        ${loading
                ? 'animate-spin bg-gray-100 dark:bg-gray-800 text-orange-500'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400'
              }
      `}
          >
            <FaSync className="text-xs sm:text-sm" />
          </button>
        </div>

        {/* Fluid Status Filter Tabs */}
        <div className="flex gap-1.5 sm:gap-3 mt-2 sm:mt-5 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
          {[
            { id: 'all', label: 'All', count: stats.total, color: 'bg-gray-400' },
            { id: 'available', label: 'Ready', count: stats.available, color: 'bg-emerald-500' },
            { id: 'occupied', label: 'Dining', count: stats.occupied, color: 'bg-orange-500' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => dispatch({ type: 'SET_FILTER', payload: tab.id as FilterType })}
              className={`
          flex-none flex items-center gap-1.5 sm:gap-2.5 
          px-3 sm:px-5 py-1.5 sm:py-2.5 
          rounded-xl sm:rounded-2xl text-[11px] sm:text-sm font-bold whitespace-nowrap transition-all
          ${state.filter === tab.id
                  ? 'bg-gray-900 text-gray-100 dark:bg-gray-100 dark:text-gray-900 shadow-md scale-[0.98] sm:scale-105'
                  : 'bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-500'
                }
        `}
            >
              <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${tab.color}`} />
              {tab.label}
              <span className={`
          text-[9px] sm:text-[11px] px-1.5 py-0.5 rounded-md 
          ${state.filter === tab.id ? 'bg-gray-100/20 text-gray-100' : 'bg-gray-100 dark:bg-gray-900'}
        `}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full pb-24">
        {error && (
          <div className="mb-4 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm flex items-center gap-3">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Enhanced Table Grid */}
        <div className=" grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-3 ">
          {filteredTables.map((t) => (
            <div key={t.id} className="transform active:scale-95 transition-transform duration-100 @container">
              <TableCard table={t} onOpen={handleTableClick} />
            </div>
          ))}
        </div>

        {filteredTables.length === 0 && !loading && (
          <div className="text-center py-20">
            <div className="bg-gray-100 dark:bg-gray-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
              <FaUtensils size={24} />
            </div>
            <p className="text-gray-500 font-medium">No {state.filter} tables found</p>
          </div>
        )}
      </main>

      {/* Modals & Sheets */}
      <CustomerDetailsForm
        open={state.openCustomerForm}
        tableName={state.selectedTableId || ''}
        onClose={() => dispatch({ type: 'SET_OPEN_CUSTOMER_FORM', payload: false })}
        onSubmit={handleCustomerDetailsSubmit}
      />

      <MoveTableModal
        open={state.openMoveModal}
        fromTable={selectedTable}
        availableTables={tables}
        isLoading={state.moveLoading}
        onClose={() => dispatch({ type: 'SET_OPEN_MOVE_MODAL', payload: false })}
        onConfirm={async (toTableId) => {
          dispatch({ type: 'SET_MOVE_LOADING', payload: true });
          try {
            if (!state.selectedTableId) return;
            await moveTable(state.selectedTableId, toTableId);
            dispatch({ type: 'SET_SELECTED_TABLE', payload: null });
            dispatch({ type: 'SET_FULL_TABLE_DETAILS', payload: { ...state.fullTableDetails } });
            delete state.fullTableDetails[state.selectedTableId];
            dispatch({ type: 'SET_OPEN_MOVE_MODAL', payload: false });
          } finally {
            dispatch({ type: 'SET_MOVE_LOADING', payload: false });
          }
        }}
      />
    </div>
  );
};

export default React.memo(QuickServe);